/**
 * Replay Service
 *
 * Simulates real-time market data by replaying historical bars as quotes.
 * Useful for debugging chart behavior during off-market hours.
 *
 * Features:
 * - Replay last 1000 bars from 8000 bar history
 * - Variable speed (1x to 640x)
 * - Quote interpolation for realistic intra-bar movement
 * - Keyboard shortcuts (Ctrl+Shift+1-9)
 *
 * Safety:
 * - Debug flag required (VITE_DEBUG_REPLAY_MODE=true)
 * - Visual warnings in UI
 * - Order blocking when active
 */

import quoteInterpolator from "./QuoteInterpolator";

// Throttle utility (lightweight implementation)
function throttle(func, wait) {
  let timeout = null;
  let lastArgs = null;
  let lastCallTime = 0;

  return function throttled(...args) {
    const now = Date.now();
    const timeSinceLastCall = now - lastCallTime;

    lastArgs = args;

    if (timeSinceLastCall >= wait) {
      // Enough time passed, call immediately
      lastCallTime = now;
      func.apply(this, args);
    } else if (!timeout) {
      // Schedule next call
      timeout = setTimeout(() => {
        timeout = null;
        lastCallTime = Date.now();
        if (lastArgs) {
          func.apply(this, lastArgs);
        }
      }, wait - timeSinceLastCall);
    }
  };
}

class ReplayService {
  constructor() {
    this.enabled = import.meta.env.VITE_DEBUG_REPLAY_MODE === "true";

    if (!this.enabled) return;

    // State
    this.isActive = false;
    this.isPlaying = false;
    this.currentIndex = 0;
    this.currentQuoteIndex = 0; // Index within interpolated quotes
    this.speed = 1; // Speed multiplier
    this.baseIntervalMs = 250; // 250ms between quotes at 1x (4 per second)
    this.quotesInjected = 0; // Counter for total quotes injected
    this.stressMode = false; // Stress mode: 60 quotes/second

    // Data
    this.allBars = [];
    this.loadedBars = [];
    this.replayBars = [];
    this.interpolatedQuotes = []; // All interpolated quotes for current bar
    this.symbol = null;
    this.periodicity = null;
    this.quotesPerBar = 4; // Default: 4 quotes per bar (1 every 250ms at 1x)

    // Callbacks
    this.onQuoteCallback = null;
    this.onStatusChange = null;

    // Throttled callback wrapper (66ms = ~15 updates/second max)
    this.throttledQuoteCallback = null;

    // Timers
    this.playbackTimer = null;

    console.log("[ReplayService] Initialized (enabled:", this.enabled, ")");
  }

  /**
   * Initialize replay with historical data
   * @param {Array} historicalBars - Full historical data (8000 bars)
   * @param {string} symbol - Symbol to replay
   * @param {string} periodicity - Chart periodicity (e.g., "1min", "5min")
   * @param {Function} onQuote - Callback to inject quotes
   */
  initialize(historicalBars, symbol, periodicity, onQuote) {
    if (!this.enabled) {
      console.warn(
        "[ReplayService] Not enabled. Set VITE_DEBUG_REPLAY_MODE=true",
      );
      return false;
    }

    if (!historicalBars || historicalBars.length < 1) {
      console.error("[ReplayService] Need at least 1 bar for replay");
      return false;
    }

    // Use ALL bars passed in - the caller (DebugPanel) has already split the data correctly
    this.allBars = historicalBars;

    // Calculate split: last 50% for replay, first 50% for loaded
    // This gives us the maximum replay bars while keeping some loaded context
    const replayCount = Math.max(1, Math.floor(historicalBars.length * 0.5));
    this.loadedBars = this.allBars.slice(0, -replayCount);
    this.replayBars = this.allBars.slice(-replayCount);

    this.symbol = symbol;
    this.periodicity = periodicity;
    this.onQuoteCallback = onQuote;
    this.currentIndex = 0;
    this.currentQuoteIndex = 0;

    // Create throttled callback (66ms = ~15 updates/second max)
    // This prevents React from being overwhelmed with too many updates
    this.throttledQuoteCallback = throttle(onQuote, 66);

    // Get recommended quotes per bar based on periodicity
    this.quotesPerBar =
      quoteInterpolator.getRecommendedQuotesPerBar(periodicity);

    console.log("[ReplayService] Initialized:", {
      totalBars: this.allBars.length,
      loadedBars: this.loadedBars.length,
      replayBars: this.replayBars.length,
      symbol,
      periodicity,
      quotesPerBar: this.quotesPerBar,
      baseIntervalMs: this.baseIntervalMs,
      throttledUpdatesPerSecond: "~15/s (66ms throttle)",
    });

    return true;
  }

  /**
   * Start replay mode
   * @param {Function} onStatusChange - Callback for status updates
   */
  start(onStatusChange) {
    if (!this.enabled || !this.replayBars.length) {
      console.error("[ReplayService] Cannot start - not initialized");
      return false;
    }

    this.isActive = true;
    this.isPlaying = true;
    this.currentIndex = 0;
    this.currentQuoteIndex = 0;
    this.interpolatedQuotes = [];
    this.quotesInjected = 0; // Reset counter on start
    this.onStatusChange = onStatusChange;

    console.log("[ReplayService] Started replay mode");
    this.notifyStatusChange();

    // Start playback
    this.scheduleNext();

    return true;
  }

  /**
   * Stop replay mode
   */
  stop() {
    if (this.playbackTimer) {
      clearTimeout(this.playbackTimer);
      this.playbackTimer = null;
    }

    this.isActive = false;
    this.isPlaying = false;
    this.currentIndex = 0;

    console.log("[ReplayService] Stopped replay mode");
    this.notifyStatusChange();
  }

  /**
   * Pause playback
   */
  pause() {
    if (!this.isActive) return;

    this.isPlaying = false;

    if (this.playbackTimer) {
      clearTimeout(this.playbackTimer);
      this.playbackTimer = null;
    }

    console.log("[ReplayService] Paused");
    this.notifyStatusChange();
  }

  /**
   * Resume playback
   */
  resume() {
    if (!this.isActive || this.isPlaying) return;

    this.isPlaying = true;
    console.log("[ReplayService] Resumed");
    this.notifyStatusChange();

    this.scheduleNext();
  }

  /**
   * Set playback speed
   * @param {number} speed - Speed multiplier (1, 5, 10, 20, 40, 80, 160, 320, 640)
   */
  setSpeed(speed) {
    this.speed = speed;
    console.log("[ReplayService] Speed set to", speed + "x");
    this.notifyStatusChange();
  }

  /**
   * Toggle stress mode (60 quotes/second)
   * @param {boolean} enabled - Enable stress mode
   */
  setStressMode(enabled) {
    this.stressMode = enabled;
    console.log(
      "[ReplayService] Stress mode",
      enabled ? "enabled" : "disabled",
      "(60 quotes/second)",
    );
    this.notifyStatusChange();
  }

  /**
   * Play next quote
   */
  playNext() {
    // Check if we should stop
    if (!this.isPlaying) {
      console.log("[ReplayService] playNext: not playing, returning");
      return;
    }

    if (this.currentIndex >= this.replayBars.length) {
      console.log("[ReplayService] Replay finished - reached end");
      this.pause();
      return;
    }

    // Generate interpolated quotes for current bar if needed
    if (this.interpolatedQuotes.length === 0) {
      const currentBar = this.replayBars[this.currentIndex];
      const prevBar =
        this.currentIndex > 0 ? this.replayBars[this.currentIndex - 1] : null;

      // Calculate quotes per bar based on stress mode
      // Stress mode: generate more quotes to actually reach 60/sec
      // For 1min bars: 60 quotes/bar gives 60 quotes/sec at 1x
      let quotesPerBar = this.quotesPerBar;
      if (this.stressMode) {
        // For stress mode, we want 60 quotes per second
        // For 1min bars: 60 quotes per bar
        // For 5min bars: 300 quotes per bar (60 * 5)
        const periodValue = parseInt(
          this.periodicity.match(/\d+/)?.[0] || "1",
          10,
        );
        const isMinute = this.periodicity.includes("min");

        if (isMinute) {
          quotesPerBar = 60 * periodValue; // 60 quotes per minute
        } else {
          quotesPerBar = 60; // Default to 60 for other periodicities
        }
      }

      this.interpolatedQuotes = quoteInterpolator.interpolate(
        prevBar,
        currentBar,
        this.symbol,
        quotesPerBar,
      );

      this.currentQuoteIndex = 0;

      if (this.interpolatedQuotes.length === 0) {
        console.error("[ReplayService] No quotes generated! Stopping.");
        this.pause();
        return;
      }
    }

    // Get next interpolated quote
    const quote = this.interpolatedQuotes[this.currentQuoteIndex];

    if (!quote) {
      console.error(
        "[ReplayService] No quote at index",
        this.currentQuoteIndex,
        "! Stopping.",
      );
      this.pause();
      return;
    }

    // Inject quote (throttled to prevent React overload)
    if (this.throttledQuoteCallback) {
      this.throttledQuoteCallback(quote);
      this.quotesInjected++; // Increment counter
    } else if (this.onQuoteCallback) {
      // Fallback to unthrottled (shouldn't happen if initialized properly)
      this.onQuoteCallback(quote);
      this.quotesInjected++;
    } else {
      console.warn("[ReplayService] No onQuoteCallback set!");
    }

    this.currentQuoteIndex++;

    // Move to next bar when all quotes sent
    if (this.currentQuoteIndex >= this.interpolatedQuotes.length) {
      this.currentIndex++;
      this.interpolatedQuotes = [];
      this.currentQuoteIndex = 0;
    }

    this.notifyStatusChange();

    // Schedule next
    this.scheduleNext();
  }

  /**
   * Schedule next quote playback
   */
  scheduleNext() {
    if (!this.isPlaying) return;

    // Calculate delay based on stress mode and speed
    let delay;
    if (this.stressMode) {
      // Stress mode: 60 quotes/second base, then apply speed multiplier
      // At 1x: 60 quotes/sec (~16.67ms)
      // At 10x: 600 quotes/sec (~1.67ms)
      // At 640x: 38,400 quotes/sec (~0.026ms)
      delay = 1000 / 60 / this.speed;
    } else {
      // Normal mode: 250ms base interval at 1x speed
      // At 10x speed: 25ms between quotes
      // At 640x speed: ~0.39ms between quotes
      delay = this.baseIntervalMs / this.speed;
    }

    this.playbackTimer = setTimeout(() => {
      this.playNext();
    }, delay);
  }

  /**
   * Notify status change
   */
  notifyStatusChange() {
    if (this.onStatusChange) {
      this.onStatusChange({
        isActive: this.isActive,
        isPlaying: this.isPlaying,
        speed: this.speed,
        currentIndex: this.currentIndex,
        totalBars: this.replayBars.length,
        progress:
          this.replayBars.length > 0
            ? (this.currentIndex / this.replayBars.length) * 100
            : 0,
      });
    }
  }

  /**
   * Get current status
   * @returns {Object} Status object
   */
  getStatus() {
    // Return safe defaults when service is not enabled
    if (!this.enabled) {
      return {
        enabled: false,
        isActive: false,
        isPlaying: false,
        speed: 1,
        currentIndex: 0,
        totalBars: 0,
        progress: 0,
        symbol: null,
        periodicity: null,
        quotesInjected: 0,
        stressMode: false,
      };
    }

    const totalBars = this.replayBars?.length || 0;
    return {
      enabled: this.enabled,
      isActive: this.isActive,
      isPlaying: this.isPlaying,
      speed: this.speed,
      currentIndex: this.currentIndex,
      totalBars,
      progress: totalBars > 0 ? (this.currentIndex / totalBars) * 100 : 0,
      symbol: this.symbol,
      periodicity: this.periodicity,
      quotesInjected: this.quotesInjected,
      stressMode: this.stressMode,
    };
  }

  /**
   * Get loaded bars (initial data for chart)
   * @returns {Array} Loaded bars
   */
  getLoadedBars() {
    return this.loadedBars;
  }

  /**
   * Get replay bars (bars used for replay)
   * @returns {Array} Replay bars
   */
  getReplayBars() {
    return this.replayBars;
  }
}

// Singleton instance
const replayService = new ReplayService();

export default replayService;
